import { HolderOutlined } from "@ant-design/icons"
import { type DraggableAttributes } from "@dnd-kit/core"
import { type SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities"
import { Button } from "antd"
import { createContext, useContext, useMemo } from "react"

interface DragHandleContextValue {
  setActivatorNodeRef?: (element: HTMLElement | null) => void
  listeners?: SyntheticListenerMap
  /** `role`, `tabIndex`, `aria-roledescription`, `aria-describedby`. These
   *  belong on the activator, not the sortable node — on a `<tr>` they would
   *  override the implicit `row` role and make every row a tab stop. */
  attributes?: DraggableAttributes
}

/**
 * Carries a sortable's activator down to its grip.
 *
 * The grip is usually rendered somewhere the sortable cannot reach directly — a
 * table cell, a tab label — so the sortable publishes its activator here and the
 * grip picks it up. Keeping the listeners on the grip alone means dragging never
 * competes with clicking the row or tab it belongs to.
 */
export const DragHandleContext = createContext<DragHandleContextValue>({})

/**
 * Packs a `useSortable` result into a stable context value for its grip.
 *
 * Memoised because the provider sits above the sortable node: a fresh object
 * each render re-renders every grip under it, mid-drag included. Pass
 * `attributes` here and nowhere else — see the field's note above.
 */
export function useDragHandleValue({
  setActivatorNodeRef,
  listeners,
  attributes
}: DragHandleContextValue): DragHandleContextValue {
  return useMemo(
    () => ({ setActivatorNodeRef, listeners, attributes }),
    [setActivatorNodeRef, listeners, attributes]
  )
}

interface DragHandleProps {
  /** Accessible name, e.g. "Reorder milestone". */
  label: string
  hidden?: boolean
  /** Greyed and inert, but still occupying its cell — `hidden` removes it and
   *  takes the column's width with it. */
  disabled?: boolean
}

export function DragHandle({ label, hidden, disabled }: DragHandleProps) {
  const { setActivatorNodeRef, listeners, attributes } =
    useContext(DragHandleContext)

  if (hidden) return null

  return (
    <Button
      type={"text"}
      size={"small"}
      aria-label={label}
      disabled={disabled}
      icon={<HolderOutlined />}
      style={{ cursor: disabled ? "not-allowed" : "move" }}
      ref={setActivatorNodeRef}
      onClick={(e) => e.stopPropagation()}
      {...(disabled ? {} : attributes)}
      {...(disabled ? {} : listeners)}
    />
  )
}
